// Learner changes made outside the console — the Windows adapter dialog and the physical cable
// inspector (src/ui/workspace.js). They live here, not in the UI, so the browser and the server
// (src/core/labVerify.js) apply exactly the same change from the same input. Every operation
// validates its input and returns the action payload to log, or null when it is not allowed.
import { setStatic, setDhcp, validateStatic } from '../engine/pc/winshell.js';
import { isHost } from '../engine/net/sim.js';

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
const str = (v, max = 64) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const linkById = (rt, id) => (typeof id === 'string' ? rt.net?.links?.find((l) => l.id === id) : null);

const OPS = {
  /** Windows "IPv4 properties" dialog: { device, dhcp:true } or { device, ip, mask, gw, dns:[x] }. */
  adapter(rt, p) {
    if (!rt.unit.adapterGui) return null;
    const d = rt.net?.devices?.[p.device];
    if (!d || !isHost(d)) return null;
    if (p.dhcp === true) { setDhcp(rt.net, d); return { device: d.id, dhcp: true }; }
    const dns0 = str(Array.isArray(p.dns) ? p.dns[0] : '');
    const cfg = { ip: str(p.ip), mask: str(p.mask), gw: str(p.gw) || null, dns: dns0 ? [dns0] : [] };
    if (validateStatic(cfg)) return null;
    if (dns0 && !IPV4.test(dns0)) return null;
    setStatic(d, cfg);
    return { device: d.id, ...cfg };
  },
  /** Reseat both connectors of a cable. */
  reseat(rt, p) {
    const l = linkById(rt, p.link);
    if (!l) return null;
    l.cable.seated = true;
    return { link: l.id };
  },
  /**
   * Desk activity (src/ui/secWidgets.js deskSwap): unplug the registered PC and plug in another
   * device, or put the original back. Only the source MAC the switch sees changes.
   * { widget, plugged: 'visitor' | 'original' } — host and visitor MAC come from the widget definition.
   */
  'desk-swap'(rt, p) {
    const w = rt.widgetDef(p.widget);
    const d = w?.deskSwap ? rt.net?.devices?.[w.deskSwap.host] : null;
    if (!d || !['visitor', 'original'].includes(p.plugged)) return null;
    const data = rt.widget(w.id).data;
    data.orig ||= d.nic.mac;
    const mac = p.plugged === 'visitor' ? w.deskSwap.mac : data.orig;
    d.nic.mac = mac; d.arp = {};
    return { widget: w.id, host: d.id, mac, plugged: p.plugged };
  },
  /** Replace a cable with one from the unit's spares cabinet (unit.physical.replacements). */
  'replace-cable'(rt, p) {
    const l = linkById(rt, p.link);
    const o = (rt.unit.physical?.replacements || []).find((x) => x.id === p.option);
    if (!l || !o || o.fits === false) return null;
    Object.assign(l.cable, { type: o.type, length: o.length, connector: o.connector, damaged: false, seated: true, compatible: true });
    return { link: l.id, option: o.id };
  },
};

export const OP_NAMES = Object.keys(OPS);

/** Apply one operation. Returns the payload to log, or null (nothing changed). */
export function applyOp(rt, name, payload) {
  const fn = Object.hasOwn(OPS, name) ? OPS[name] : null;
  if (!fn || !payload || typeof payload !== 'object') return null;
  try { return fn(rt, payload); } catch { return null; }
}
